import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ExternalLink, Check } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import useT from '../i18n';
import { translateApiError } from '../i18n/apiErrors';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MIN_DWELL_MS = 3000; // accept-button gating fallback (3 s)

/**
 * TermsAcceptanceModal — forced (non-dismissible) modal that gates the app
 * for both fagarbeidere AND kommuner whose accepted ToS version is
 * missing or stale for their role's document_type.
 *
 * Mounted globally. Routes role → document_type:
 *   role=professional → document_type=professional
 *   role=employer     → document_type=employer
 *   role=admin        → no modal
 *
 * Reads acceptance state from BOTH the new role-prefixed fields
 * (`<role>_terms_accepted_version`) AND the legacy `terms_accepted_version`
 * field — first non-null wins. This keeps existing fagarbeider data working
 * after the employer split landed.
 *
 * Accept button unlocks after EITHER (a) the user scrolls to the bottom of
 * the modal content, OR (b) 3 seconds have elapsed since mount.
 *
 * On accept → POST /api/me/terms-accept (with document_type) → AuthContext.refreshUser().
 */
export default function TermsAcceptanceModal() {
  const { user, refreshUser } = useAuth();
  const { language } = useLanguage();
  const t = useT();

  const [open, setOpen] = useState(false);
  const [doc, setDoc] = useState(null);
  const [docType, setDocType] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [scrolledToEnd, setScrolledToEnd] = useState(false);
  const [dwellOk, setDwellOk] = useState(false);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);

  // Decide which document_type (if any) the user must accept.
  useEffect(() => {
    if (!user || (user.role !== 'professional' && user.role !== 'employer')) {
      setOpen(false);
      setDocType(null);
      return;
    }
    const dt = user.role === 'employer' ? 'employer' : 'professional';
    setDocType(dt);
    let mounted = true;
    axios
      .get(`${API_URL}/api/terms/current`, {
        params: { document_type: dt, locale: language || 'no' },
      })
      .then((res) => {
        if (!mounted) return;
        setDoc(res.data);
        // Prefer the role-prefixed field; fall back to legacy.
        const acceptedV =
          user[`${dt}_terms_accepted_version`] || user.terms_accepted_version;
        if (!acceptedV || acceptedV !== res.data.version) {
          setOpen(true);
        } else {
          setOpen(false);
        }
      })
      .catch(() => {
        if (mounted) setOpen(false);
      });
    return () => {
      mounted = false;
    };
  }, [
    user?.id,
    user?.role,
    user?.terms_accepted_version,
    user?.professional_terms_accepted_version,
    user?.employer_terms_accepted_version,
    language,
  ]);

  // Dwell timer (3 s)
  useEffect(() => {
    if (!open) return;
    setDwellOk(false);
    const id = setTimeout(() => setDwellOk(true), MIN_DWELL_MS);
    return () => clearTimeout(id);
  }, [open]);

  // Scroll-to-bottom detector
  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 16) {
      setScrolledToEnd(true);
    }
  };

  // Block ESC / click-outside while open. Body-scroll lock.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') e.preventDefault();
    };
    document.addEventListener('keydown', onKey, true);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  const handleAccept = async () => {
    if (!doc) return;
    setSubmitting(true);
    setError(null);
    const token = localStorage.getItem('access_token');
    try {
      await axios.post(
        `${API_URL}/api/me/terms-accept`,
        { version: doc.version, document_type: docType || 'professional' },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      await refreshUser();
      setOpen(false);
    } catch (err) {
      setError(translateApiError(err.response?.data?.detail) || t.employment?.form?.errorGeneric || 'Error');
    } finally {
      setSubmitting(false);
    }
  };

  const canAccept = (scrolledToEnd || dwellOk) && !submitting && doc;

  if (!open || !doc) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        className="fixed inset-0 z-[100] bg-charcoal/75 backdrop-blur-sm flex items-center justify-center px-4 py-8"
        data-testid="terms-modal-overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby="terms-modal-heading"
      >
        <motion.div
          key="card"
          initial={{ opacity: 0, y: 18, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 10 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="bg-paper text-ink dark:bg-stone-900 dark:text-stone-100 rounded-md w-full max-w-2xl max-h-[88vh] flex flex-col overflow-hidden"
          data-testid="terms-modal"
        >
          {/* Header */}
          <div className="px-7 py-6 border-b border-mist dark:border-stone-800">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] font-semibold text-fjord mb-2">
              {t.terms.pageEyebrow}
            </div>
            <h2
              id="terms-modal-heading"
              className="font-display text-2xl md:text-3xl font-medium leading-tight"
              data-testid="terms-modal-heading"
            >
              {t.terms.modal.heading}
            </h2>
            <p className="mt-2 text-sm text-mute dark:text-stone-400">{t.terms.modal.subtext}</p>
          </div>

          {/* Scrollable ToS body */}
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className="px-7 py-6 overflow-y-auto flex-1"
            data-testid="terms-modal-body"
          >
            <div
              className="terms-prose terms-prose-modal font-body"
              dangerouslySetInnerHTML={{ __html: doc.content_html }}
            />
          </div>

          {/* Footer with controls */}
          <div className="px-7 py-5 border-t border-mist dark:border-stone-800 bg-bone/40 dark:bg-stone-900/40 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
            <a
              href={
                docType === 'employer'
                  ? language === 'en' ? '/terms/employer' : '/vilkar/kommune'
                  : language === 'en' ? '/terms/professional' : '/vilkar/fagarbeider'
              }
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-mute hover:text-ink dark:text-stone-400 dark:hover:text-stone-100 transition-colors"
              data-testid="terms-modal-open-newtab"
            >
              <ExternalLink size={14} />
              {t.terms.modal.openInNewTab}
            </a>

            <div className="flex flex-col items-end gap-1.5">
              {error && (
                <span className="text-xs text-clay font-medium" role="alert">
                  {error}
                </span>
              )}
              {!canAccept && (
                <span
                  className="text-[11px] text-mute italic"
                  data-testid="terms-modal-accept-hint"
                >
                  {t.terms.modal.acceptHint}
                </span>
              )}
              <button
                type="button"
                onClick={handleAccept}
                disabled={!canAccept}
                className="group inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm font-semibold bg-ink text-paper hover:bg-ink transition-all disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-fjord/40 focus-visible:ring-offset-2 focus-visible:ring-offset-paper min-h-[44px]"
                data-testid="terms-modal-accept-button"
              >
                {submitting ? t.terms.modal.submitting : t.terms.modal.accept}
                {!submitting && <Check size={15} className="opacity-90" />}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
